import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Chip,
  Divider,
  Grid,
  LinearProgress,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";

import RepricingPoReview from "./components/RepricingPoReview";
import RepricingOverview from "./components/RepricingOverview";
import RepricingRunsReview from "./components/RepricingRunsReview";
import RepricingStoreSignals from "./components/RepricingStoreSignals";
import RepricingListingCandidates from "./components/RepricingListingCandidates";
import RepricingPushQueue from "./components/RepricingPushQueue";
import RepricingOutcomeLoop from "./components/RepricingOutcomeLoop";
import RepricingCampaignModes from "./components/RepricingCampaignModes";
import RepricingWeeklyRunbook from "./components/RepricingWeeklyRunbook";

const API_BASE = "/api/repricing";

const FALLBACK_SHELL = {
  ok: true,
  title: "Repricing",
  mode: "foundation_shell",
  read_only: true,
  mutation_enabled: false,
  tabs: [
    {
      key: "weekly_runbook",
      label: "Weekly Runbook",
      status: "active",
      description: "Guided weekly repricing operating checklist with status indicators and links.",
      signals: ["latest_run", "stale_ebay_data", "approved_push_queue", "failed_pushes"],
    },
    {
      key: "campaign_modes",
      label: "Campaign Modes",
      status: "active",
      description: "Deterministic campaign presets for weekly, slowdown, PO, trainer, and margin-protection reviews.",
      signals: ["normal_weekly", "store_slowdown_boost", "po_cleanup", "capital_recovery"],
    },
    {
      key: "po_performance",
      label: "PO Performance",
      status: "active",
      description: "Read-only purchase-order sell-through, capital recovery, and remaining stale stock review.",
      signals: ["purchase_order_id", "expected_profit", "expected_margin", "last_ebay_sync_at"],
    },
    {
      key: "store_signals",
      label: "Store Signals",
      status: "active",
      description: "Listing traffic, ad performance, sync health, and offer-state context.",
      signals: [
        "ebay_listing_snapshots",
        "ebay_listing_traffic_daily",
        "ebay_ad_listing_performance_daily",
        "ebay_action_outcomes",
      ],
    },
    {
      key: "candidate_preview",
      label: "Candidate Preview",
      status: "active",
      description: "Read-only candidate grouping for later recommendation and approval flows.",
      signals: ["ebay_offer_id", "ebay_listing_id", "current_price", "data_quality_warnings"],
    },
    {
      key: "runs",
      label: "Runs",
      status: "active",
      description: "Persisted run headers and local-only recommendation review workflow.",
      signals: ["accept", "reject", "hold", "manual_override", "blocked_reasons"],
    },
    {
      key: "push_queue",
      label: "Push Queue",
      status: "active",
      description: "Approved recommendation rows waiting for explicit eBay price push.",
      signals: ["approved_price", "push_blockers", "confirm_push", "post_push_refresh"],
    },
    {
      key: "outcome_loop",
      label: "Outcome Loop",
      status: "active",
      description: "Read-only action history and post-push sales/profit measurement for repricing recommendations.",
      signals: ["repricing_price_push", "last_price_push_at", "sales_after_action", "item_profit"],
    },
    {
      key: "guardrails",
      label: "Guardrails",
      status: "active",
      description: "Safety boundaries for local-only recommendation review.",
      signals: ["no_price_mutation", "no_ebay_mutation", "no_repricing_rows_persisted"],
    },
  ],
  overview_cards: [],
  store_signal_warnings: [],
  data_sources: [
    {
      name: "listings",
      fields: [
        "ebay_offer_id",
        "ebay_listing_id",
        "purchase_order_id",
        "expected_profit",
        "expected_margin",
        "last_ebay_sync_at",
      ],
    },
    {
      name: "eBay Intelligence telemetry",
      fields: [
        "ebay_listing_snapshots",
        "ebay_listing_traffic_daily",
        "ebay_ad_listing_performance_daily",
        "ebay_sync_import_runs",
        "ebay_action_outcomes",
      ],
    },
  ],
  safety_boundaries: [
    "Only the Push Queue tab can call the controlled eBay price mutation endpoint.",
    "Push Queue requires explicit confirmation and selected approved rows.",
    "Recommendation review state is persisted only in repricing_recommendations.",
    "Local listing price and last push fields update only after eBay confirms success.",
  ],
};

const getInitialRepricingTab = () => {
  const params = new URLSearchParams(window.location.search || "");
  return params.get("repricing_tab") || params.get("tab") || FALLBACK_SHELL.tabs[0].key;
};

const updateRepricingQuery = (updates = {}) => {
  const params = new URLSearchParams(window.location.search || "");
  Object.entries(updates).forEach(([key, value]) => {
    if (value === null || value === undefined || value === "") {
      params.delete(key);
    } else {
      params.set(key, String(value));
    }
  });
  const nextUrl = `${window.location.pathname}?${params.toString()}${window.location.hash || ""}`;
  window.history.replaceState({}, "", nextUrl);
};

const statusColor = (status) => {
  if (status === "active") return "success";
  if (status === "placeholder") return "default";
  return "warning";
};

export default function RepricingDashboard() {
  const [shell, setShell] = useState(FALLBACK_SHELL);
  const [activeTab, setActiveTab] = useState(getInitialRepricingTab);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;

    async function loadShell() {
      setLoading(true);
      setError("");
      try {
        const res = await fetch(`${API_BASE}/overview`);
        const data = await res.json();
        if (!res.ok || !data.ok) {
          throw new Error(data.error || "Repricing shell endpoint unavailable");
        }
        if (!cancelled) {
          setShell({ ...FALLBACK_SHELL, ...data });
          const firstTab = data.tabs?.[0]?.key || FALLBACK_SHELL.tabs[0].key;
          setActiveTab((current) =>
            data.tabs?.some((tab) => tab.key === current) ? current : firstTab
          );
        }
      } catch (e) {
        if (!cancelled) {
          setShell(FALLBACK_SHELL);
          setError(e.message || String(e));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadShell();
    return () => {
      cancelled = true;
    };
  }, []);

  const tabs = useMemo(() => {
    const sourceTabs = shell.tabs?.length ? shell.tabs : FALLBACK_SHELL.tabs;
    return ["push_queue", "outcome_loop"].reduce((nextTabs, key) => {
      if (nextTabs.some((tab) => tab.key === key)) return nextTabs;
      const fallbackTab = FALLBACK_SHELL.tabs.find((tab) => tab.key === key);
      return fallbackTab ? [...nextTabs, fallbackTab] : nextTabs;
    }, sourceTabs);
  }, [shell.tabs]);
  const currentTab = useMemo(
    () => tabs.find((tab) => tab.key === activeTab) || tabs[0],
    [activeTab, tabs]
  );

  const openRepricingTab = (tabKey, query = {}) => {
    if (!tabs.some((tab) => tab.key === tabKey)) return;
    setActiveTab(tabKey);
    updateRepricingQuery({ repricing_tab: tabKey, ...query });
  };

  return (
    <Box>
      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", md: "center" }}
        spacing={1.5}
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Repricing
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Preparing Listings foundation
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Chip size="small" color="primary" label="Read only" />
          <Chip size="small" label="No price push" />
          <Chip size="small" label="No eBay mutation" />
        </Stack>
      </Stack>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {error && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {error}. Showing local shell metadata.
        </Alert>
      )}

      <Alert severity="info" sx={{ mb: 2 }}>
        Repricing keeps recommendation review separate from price mutation. The Push Queue tab is
        the only controlled path here that can request an eBay price change, and it requires
        selected approved rows plus explicit confirmation.
      </Alert>

      <RepricingOverview shell={shell} />

      <Tabs
        value={currentTab.key}
        onChange={(e, value) => openRepricingTab(value)}
        variant="scrollable"
        scrollButtons="auto"
        textColor="primary"
        indicatorColor="primary"
        sx={{ mb: 2 }}
      >
        {tabs.map((tab) => (
          <Tab key={tab.key} value={tab.key} label={tab.label} sx={{ textTransform: "none" }} />
        ))}
      </Tabs>

      <Grid container spacing={2}>
        <Grid item xs={12} md={8}>
          <Paper variant="outlined" sx={{ p: 2, height: "100%" }}>
            <Stack spacing={1.5}>
              <Stack direction="row" justifyContent="space-between" spacing={1} alignItems="center">
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {currentTab.label}
                </Typography>
                <Chip
                  size="small"
                  color={statusColor(currentTab.status)}
                  label={currentTab.status || "planned"}
                />
              </Stack>
              <Typography variant="body2" color="text.secondary">
                {currentTab.description}
              </Typography>
              <Divider />
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {(currentTab.signals || []).map((signal) => (
                  <Chip key={signal} size="small" variant="outlined" label={signal} />
                ))}
              </Stack>
            </Stack>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper variant="outlined" sx={{ p: 2, height: "100%" }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
              Guardrails
            </Typography>
            <Stack spacing={1}>
              {(shell.safety_boundaries || FALLBACK_SHELL.safety_boundaries).map((boundary) => (
                <Typography key={boundary} variant="body2" color="text.secondary">
                  {boundary}
                </Typography>
              ))}
            </Stack>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          {currentTab.key === "weekly_runbook" ? (
            <RepricingWeeklyRunbook onOpenTab={openRepricingTab} />
          ) : currentTab.key === "campaign_modes" ? (
            <RepricingCampaignModes onOpenTab={openRepricingTab} />
          ) : currentTab.key === "po_performance" ? (
            <RepricingPoReview onOpenTab={openRepricingTab} />
          ) : currentTab.key === "store_signals" ? (
            <RepricingStoreSignals />
          ) : currentTab.key === "candidate_preview" ? (
            <RepricingListingCandidates />
          ) : currentTab.key === "runs" ? (
            <RepricingRunsReview />
          ) : currentTab.key === "push_queue" ? (
            <RepricingPushQueue />
          ) : currentTab.key === "outcome_loop" ? (
            <RepricingOutcomeLoop />
          ) : (
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
              Data Sources
            </Typography>
            <Grid container spacing={2}>
              {(shell.data_sources || FALLBACK_SHELL.data_sources).map((source) => (
                <Grid item xs={12} md={6} key={source.name}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                    {source.name}
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {(source.fields || []).map((field) => (
                      <Chip key={field} size="small" variant="outlined" label={field} />
                    ))}
                  </Stack>
                </Grid>
              ))}
            </Grid>
          </Paper>
          )}
        </Grid>
      </Grid>
    </Box>
  );
}
